import { Redirect } from 'react-router-dom';

// Switch logic without a default case and with fallthrough is by design here, so we disable those warnings
/* eslint-disable no-fallthrough, default-case */

class DestinationSwitch {
  constructor(props) {
    this.props = props;
  }

  redirectFunctions = {
    arrivingPage: () => this.arrivingPage(),
    leavingPage: () => this.leavingPage(),
    profilesPage: () => this.profilesPage(),
    arrivingOrLeavingPage: () => this.arrivingOrLeavingPage(),
    autocompletePage: () => this.autocompletePage(),
    customAttributesPage: () => this.customAttributesPage(),
    covidQRPage: () => this.covidQRPage(),
    groupArrivalsPage: () => this.groupArrivalsPage(),
    visitingWhoPage: () => this.visitingWhoPage(),
    groupLeavingsPage: (group) => this.groupLeavingsPage(group),
    feedbackPage: (feedbackTypes) => this.feedbackPage(feedbackTypes),
    informationPage: () => this.informationPage(),
    thankYouPage: () => this.thankYouPage()
  }

  redirect() {
    const nextPage = this.getNextPage();

    return this.redirectFunctions[nextPage.page](...nextPage.args);
  }

  buttonName() {
    let next_page = null

    try {
      next_page = this.getNextPage().page;
    } catch (error) {
      next_page = null;
    }

    return next_page === "thankYouPage" ? "Submit" : "Next"
  }

  getNextPage() {
    switch (this.props.current_page) {
      case "arriving-or-leaving":
        if (this.props.profile && this.props.direction === "arriving") {
          return { page: "arrivingPage", args: [] }
        }
        else if (this.props.profile && this.props.direction === "leaving") {
          return { page: "leavingPage", args: [] }
        }
        else {
          return { page: "profilesPage", args: [] }
        }
      case "profiles":
        if (this.props.direction === "arriving") {
          return { page: "arrivingPage", args: [] }
        }
        else if (this.props.direction === "leaving") {
          return { page: "leavingPage", args: [] }
        }
        else {
          return { page: "arrivingOrLeavingPage", args: [] }
        }
      // arriving only
      case "arriving":
        if (this.autocompleteDisabled()) {
          return { page: "autocompletePage", args: [] }
        }
      case "autocomplete":
        if (this.containsPostArrivalCustomAttributes()) {
          return { page: "customAttributesPage", args: [] }
        }
      case "custom-attributes":
        if (this.covidScanRequired()) {
          return { page: "covidQRPage", args: [] }
        }
      case "covid-qr":
        if (this.groupArrivalsEnabled()) {
          return { page: "groupArrivalsPage", args: [] }
        }
      case "group-arrivals":
        if (this.unplannedEvent() && this.canVisitPeople() && this.hasPeopleToVisit()) {
          return { page: "visitingWhoPage", args: [] }
        }
      // arriving or leaving
      case "leaving":
        const group = this.findGroup()

        if (this.groupArrivalsEnabled() && group !== undefined && group.length > 0 && this.props.personal !== true) {
          return { page: "groupLeavingsPage", args: [group] }
        }
      case "group-leavings":
      case "visiting-who":
        let feedbackTypes = this.calculateFeedbackTypes()
        if (feedbackTypes.length > 0) {
          return { page: "feedbackPage", args: [feedbackTypes] }
        }
      case "feedback":
        if (this.containsInformation()) {
          return { page: "informationPage", args: [] }
        }

      case "information":
        return { page: "thankYouPage", args: [] }
    }
  }

  renderURL(path) {
    if (this.props.personal) {
      return "/personal" + path
    }
    else {
      return path
    }
  }

  // Conditional logic
  containsPostArrivalCustomAttributes() {
    // always skip if Profile requires no Custom Attributes
    if (this.props.profile.custom_attributes && this.props.profile.custom_attributes.length === 0) {
      return false
    }

    // take a note of pre-existing Person Custom Attributes with a value of "Acknowledged", if any
    const acknowledgedCustomAttributeIDs = this.props.person.custom_attributes.filter(
      (person_custom_attribute) => person_custom_attribute.value === "Acknowledged"
    ).map(
      (person_custom_attribute) => person_custom_attribute.id
    );

    // `Required` `Text` Custom Attributes are now shown on the Arrivals page.
    // Filtering them out of this collection.
    const postArrivalCustomAttributes = this.props.profile.custom_attributes.filter(
      attribute => !(attribute.required === true && attribute.answer_type === "text")
    )

    // filter out their corresponding Profile Custom Attributes if they are also currently set as the "acknowledgement" answer_type
    const shownCustomAttributes = postArrivalCustomAttributes.filter(
      (profile_custom_attribute) => profile_custom_attribute.answer_type !== "acknowledgement" || !acknowledgedCustomAttributeIDs.includes(profile_custom_attribute.id)
    );

    return shownCustomAttributes.length > 0
  }

  covidScanRequired() {
    return (this.props.profile.covid_pass_check_required && new Date(this.props.person.covid_pass_expiry_date) < new Date().setHours(0,0,0,0) && this.props.person.covid_pass_exemption !== true)
  }

  groupArrivalsEnabled() {
    if (this.props.profile.group_arrivals === true && this.props.personal !== true) {
      return true
    }
  }

  findGroup() {
    const group_id = this.props.person?.group_id
    const groups = JSON.parse(localStorage.checked_in_groups || "{}")

    if (group_id) {
      return groups[group_id]
    }
  }

  unplannedEvent() {
    return (this.props.planned_event_ids.length === 0)
  }

  canVisitPeople() {
    // TODO: Remove skip_visiting_people clause (deprecated)
    return (this.props.profile.ask_visiting_people !== "never" && this.props.profile.skip_visiting_people !== true)
  }

  hasPeopleToVisit() {
    return (this.hasRelationships() || this.hasVisitAllEnabled() || this.props.profile.visitable_types_of_people.length > 0)
  }

  hasRelationships() {
    return (this.props.person.relationships !== undefined && this.props.person.relationships.length > 0)
  }

  hasVisitAllEnabled() {
    return (this.props.person.visit_all !== null && this.props.person.visit_all !== undefined)
  }

  calculateFeedbackTypes() {
    let feedbackTypes = [];

    if (this.props.direction === "arriving") {
      if (this.props.profile.feedback_on_arrival === true) {
        this.props.profile.feedback_types.forEach((feedback) => {
          if (feedback.show_on === "Arrival" && feedback.feedback_questions.length > 0) {
            feedbackTypes.push(feedback)
          }
        })
      }
    }
    else {
      this.props.profile.feedback_types.forEach((feedback) => {
        if (feedback.show_on === "Departure" && feedback.feedback_questions.length > 0) {
            feedbackTypes.push(feedback)
        }
      })
    }

    if (this.props.person.feedback_answer_times !== null) {
      feedbackTypes = feedbackTypes.filter((feedbackType) => {
        if (feedbackType.frequency === "always" || this.props.person.feedback_answer_times[feedbackType.id] === undefined) {
          return true
        }

        if (feedbackType.frequency === "once") {
          return feedbackType.ask_from > this.props.person.feedback_answer_times[feedbackType.id]
        }

        let answerDate = new Date(this.props.person.feedback_answer_times[feedbackType.id])
        let currentDate = this.applyServerOffset(new Date())

        switch (feedbackType.frequency) {
          case "weekly":
            return (currentDate - answerDate) > 604800000 // 1000 * 60 * 60 * 24 * 7
          case "monthly":
            return (currentDate - answerDate) > 2419200000 // 1000 * 60 * 60 * 24 * 7 * 4
          default:
            return true
        }
      })
    }

    return feedbackTypes
  }

  applyServerOffset(date) {
    let server_offset = parseInt(localStorage.getItem("server_offset"))

    if (server_offset) {
      return new Date(date - (server_offset * 1000))
    }

    return date
  }

  containsInformation() {
    if (this.props.direction === "arriving") {
      return this.props.profile.information_on_check_in || this.props.profile.check_in_info_image
    }

    return this.props.profile.information_on_check_out || this.props.profile.check_out_info_image
  }

  autocompleteDisabled() {
    return (localStorage.getItem("disable_autocomplete") === "true")
  }

  arrivingOrLeavingPage() {
    return <Redirect to={{ pathname: this.renderURL("/arriving-or-leaving"), state: { profile: this.props.profile, personal: this.props.personal } }}/>
  }

  profilesPage() {
    return <Redirect to={{ pathname: this.renderURL("/profiles"), state: { direction: this.props.direction, personal: this.props.personal } }}/>
  }

  arrivingPage() {
    return <Redirect to={{ pathname: this.renderURL("/arriving"), state: { profile: this.props.profile, direction: this.props.direction, personal: this.props.personal } }}/>
  }

  leavingPage() {
    return <Redirect to={{ pathname: this.renderURL("/leaving"), state: { profile: this.props.profile, direction: this.props.direction, personal: this.props.personal } }}/>
  }

  autocompletePage() {
    return <Redirect to={{ pathname: this.renderURL("/autocomplete"), state: { token: this.props.token, profile: this.props.profile, person: this.props.person, arriving_app_id: this.props.arriving_app_id, direction: this.props.direction, personal: this.props.personal, planned_event_ids: this.props.planned_event_ids} }}/>
  }

  customAttributesPage() {
    return <Redirect to={{ pathname: this.renderURL("/custom-attributes"), state: { token: this.props.token, profile: this.props.profile, person: this.props.person, arriving_app_id: this.props.arriving_app_id, direction: this.props.direction, personal: this.props.personal, planned_event_ids: this.props.planned_event_ids} }}/>
  }

  covidQRPage() {
    return <Redirect to={{ pathname: this.renderURL("/covid-qr"), state: { token: this.props.token, profile: this.props.profile, person: this.props.person, arriving_app_id: this.props.arriving_app_id, direction: this.props.direction, personal: this.props.personal, planned_event_ids: this.props.planned_event_ids} }}/>
  }

  groupArrivalsPage() {
    return <Redirect to={{ pathname: this.renderURL("/group-arrivals"), state: { profile: this.props.profile, person: this.props.person, arriving_app_id: this.props.arriving_app_id, planned_event_ids: this.props.planned_event_ids} }}/>
  }

  visitingWhoPage() {
    return <Redirect to={{ pathname: this.renderURL("/visiting-who"), state: { token: this.props.token, relationships: this.props.person.relationships, profile: this.props.profile, person: this.props.person, arriving_app_id: this.props.arriving_app_id, direction: this.props.direction, personal: this.props.personal} }}/>
  }

  groupLeavingsPage(group) {
    return <Redirect to={{ pathname: this.renderURL("/group-leavings"), state: { profile: this.props.profile, person: this.props.person, group: group, leaving_app_id: this.props.leaving_app_id } }}/>
  }

  feedbackPage(feedbackTypes) {
    const directional_app_id = (this.props.direction === "arriving") ? this.props.arriving_app_id : this.props.leaving_app_id

    return <Redirect to={{ pathname: this.renderURL("/feedback"), state: { token: this.props.token, profile: this.props.profile, directional_app_id: directional_app_id, direction: this.props.direction, personal: this.props.personal, feedbackTypes: feedbackTypes } }}/>
  }

  informationPage() {
    return <Redirect to={{ pathname: this.renderURL("/information"), state: { profile: this.props.profile, direction: this.props.direction, personal: this.props.personal } }}/>
  }

  thankYouPage() {
    return <Redirect to={{ pathname: this.renderURL("/thank-you"), state: { profile: this.props.profile, direction: this.props.direction, personal: this.props.personal } }}/>
  }
}

export default DestinationSwitch;
